import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2, Check, Copy, ExternalLink, ChevronDown, ChevronUp, Search, X, Film, Settings, Zap, UserCheck, Music, Camera, UtensilsCrossed } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_SLOTS = [
  { key: 'Photography', label: 'Photography', icon: Camera, color: 'blue' },
  { key: 'DJ/Music', label: 'DJ / Music', icon: Music, color: 'purple' },
  { key: 'Catering', label: 'Catering', icon: UtensilsCrossed, color: 'orange' },
];

const STATE_LABELS = {
  booked_deposit_paid: { label: 'Booked + Paid', color: 'bg-green-100 text-green-700' },
  booked_change_requested: { label: 'Booked + Change Request', color: 'bg-amber-100 text-amber-700' },
  proposal_pending: { label: 'Proposal Sent', color: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Contacted', color: 'bg-gray-100 text-gray-600' },
};

/* ── Searchable Select ─────────────────────────── */
function SearchableSelect({ placeholder, items, value, onChange, renderItem, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = items.filter(item => {
    const q = query.toLowerCase();
    return (item.name || '').toLowerCase().includes(q) ||
           (item.email || '').toLowerCase().includes(q) ||
           (item.business_name || '').toLowerCase().includes(q);
  });

  const selected = items.find(i => i.id === value);

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between border rounded-lg px-3 py-2.5 text-sm text-left transition-colors ${
          open ? 'border-gray-400 ring-2 ring-gray-200' : 'border-gray-200 hover:border-gray-300'
        } ${value ? 'bg-white' : 'bg-gray-50'}`}
      >
        <span className={value ? 'text-gray-900 font-medium' : 'text-gray-400'}>
          {selected ? renderItem(selected) : placeholder}
        </span>
        {value ? (
          <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 shrink-0" onClick={(e) => { e.stopPropagation(); onChange(null); }} />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        )}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search className="h-3.5 w-3.5 text-gray-400" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search..."
              className="flex-1 text-sm outline-none bg-transparent"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No results</p>
            ) : filtered.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => { onChange(item.id); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between ${
                  item.id === value ? 'bg-gray-50 font-medium' : ''
                }`}
              >
                <span>{renderItem(item)}</span>
                {item.id === value && <Check className="h-3.5 w-3.5 text-green-600" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Copy Button ─────────────────────────── */
function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
      data-testid={`copy-${label}`}
    >
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/* ── Main Component ─────────────────────────── */
export default function PresentationBuilder() {
  const [mode, setMode] = useState('perfect_demo');
  const [customerId, setCustomerId] = useState(null);
  const [vendorAssignments, setVendorAssignments] = useState({});
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advanced, setAdvanced] = useState({ event_type: 'Birthday Party', event_date_offset_days: 7, event_time: '7:00 PM', guest_count: 60, budget: 10000 });

  const [customers, setCustomers] = useState([]);
  const [vendorsBySlot, setVendorsBySlot] = useState({});
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch customers + vendors for each slot
  const fetchData = useCallback(async () => {
    try {
      const custRes = await fetch(`${API}/api/admin/scenarios/search-users?role=customer`, { headers });
      if (custRes.ok) setCustomers(await custRes.json());

      const slotData = {};
      for (const slot of SERVICE_SLOTS) {
        const res = await fetch(`${API}/api/admin/scenarios/search-vendors?category=${encodeURIComponent(slot.key)}`, { headers });
        if (res.ok) {
          const vendors = await res.json();
          slotData[slot.key] = vendors.filter(v => v.has_user);
        }
      }
      setVendorsBySlot(slotData);
    } catch (e) {
      console.error('Failed to load data:', e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setVendor = (slot, vendorId) => {
    setVendorAssignments(prev => {
      const next = { ...prev };
      if (vendorId) next[slot] = vendorId;
      else delete next[slot];
      return next;
    });
  };

  const canGenerate = (autoGenerate || customerId) && Object.keys(vendorAssignments).length > 0;

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    setError(null);
    try {
      const body = {
        vendor_assignments: vendorAssignments,
        scenario_type: mode,
        ...advanced,
      };
      if (autoGenerate) {
        const ts = Date.now().toString(36);
        body.new_customer_email = `demo_${ts}@celeyapp.com`;
        body.new_customer_name = `Demo User`;
        body.new_customer_password = 'Demo123!';
      } else {
        body.customer_id = customerId;
      }

      const res = await fetch(`${API}/api/admin/scenarios/presentation`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.errors?.join(', ') || data.detail || 'Generation failed');
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const vendorCount = Object.keys(vendorAssignments).length;
  const appUrl = window.location.origin;

  return (
    <div className="space-y-5" data-testid="presentation-builder">
      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-1">
          <Film className="h-5 w-5 text-amber-400" />
          <h3 className="text-lg font-semibold">Presentation Builder</h3>
        </div>
        <p className="text-sm text-gray-400">Create a complete demo scenario in one click. Select users, generate, and go.</p>
      </div>

      {/* ── Mode Selector ── */}
      <div className="flex gap-2" data-testid="mode-selector">
        <button
          onClick={() => setMode('perfect_demo')}
          className={`flex-1 flex items-center gap-2.5 px-4 py-3 rounded-lg border-2 transition-all text-left ${
            mode === 'perfect_demo'
              ? 'border-gray-900 bg-gray-50'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
          data-testid="mode-perfect-demo"
        >
          <Film className={`h-4.5 w-4.5 ${mode === 'perfect_demo' ? 'text-gray-900' : 'text-gray-400'}`} />
          <div>
            <p className={`text-sm font-semibold ${mode === 'perfect_demo' ? 'text-gray-900' : 'text-gray-600'}`}>Perfect Demo</p>
            <p className="text-[11px] text-gray-400">Pre-configured states, ready to present</p>
          </div>
        </button>
        <button
          onClick={() => setMode('targeted')}
          className={`flex-1 flex items-center gap-2.5 px-4 py-3 rounded-lg border-2 transition-all text-left ${
            mode === 'targeted'
              ? 'border-gray-900 bg-gray-50'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
          data-testid="mode-targeted"
        >
          <Settings className={`h-4.5 w-4.5 ${mode === 'targeted' ? 'text-gray-900' : 'text-gray-400'}`} />
          <div>
            <p className={`text-sm font-semibold ${mode === 'targeted' ? 'text-gray-900' : 'text-gray-600'}`}>Targeted Scenario</p>
            <p className="text-[11px] text-gray-400">All vendors get proposal-pending state</p>
          </div>
        </button>
      </div>

      {/* ── Auto-generate toggle ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="auto-generate-toggle">
          <button
            type="button"
            onClick={() => setAutoGenerate(!autoGenerate)}
            className={`relative w-10 h-5 rounded-full transition-colors ${autoGenerate ? 'bg-gray-900' : 'bg-gray-300'}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoGenerate ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
          <div>
            <span className="text-sm font-medium text-gray-800">Auto-generate demo customer</span>
            <p className="text-[11px] text-gray-400">Creates a fresh demo account (demo_xxx@celeyapp.com / Demo123!)</p>
          </div>
        </label>
      </div>

      {/* ── Customer Selection (when not auto-generating) ── */}
      {!autoGenerate && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2.5">Customer</p>
          <SearchableSelect
            testId="customer-select"
            placeholder="Select a customer..."
            items={customers}
            value={customerId}
            onChange={setCustomerId}
            renderItem={(u) => `${u.name} (${u.email})`}
          />
        </div>
      )}

      {/* ── Vendor Assignments ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Vendor Assignments</p>
        <div className="space-y-3">
          {SERVICE_SLOTS.map(slot => {
            const Icon = slot.icon;
            const vendors = vendorsBySlot[slot.key] || [];
            const slotState = mode === 'perfect_demo' ? (
              slot.key === 'Photography' ? STATE_LABELS.booked_deposit_paid :
              slot.key === 'DJ/Music' ? STATE_LABELS.booked_change_requested :
              STATE_LABELS.proposal_pending
            ) : STATE_LABELS.proposal_pending;

            return (
              <div key={slot.key} className="flex items-start gap-3" data-testid={`vendor-slot-${slot.key}`}>
                <div className={`mt-1 flex items-center justify-center h-9 w-9 rounded-lg shrink-0 ${
                  slot.color === 'blue' ? 'bg-blue-50 text-blue-600' :
                  slot.color === 'purple' ? 'bg-purple-50 text-purple-600' :
                  'bg-orange-50 text-orange-600'
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-700">{slot.label}</span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${slotState.color}`}>{slotState.label}</span>
                  </div>
                  <SearchableSelect
                    testId={`vendor-select-${slot.key}`}
                    placeholder={`Select ${slot.label.toLowerCase()} vendor...`}
                    items={vendors}
                    value={vendorAssignments[slot.key] || null}
                    onChange={(id) => setVendor(slot.key, id)}
                    renderItem={(v) => v.business_name || 'Unnamed'}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Advanced Options (collapsed) ── */}
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
        data-testid="toggle-advanced"
      >
        {showAdvanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        Advanced options
      </button>
      {showAdvanced && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="advanced-options">
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Event Type</label>
            <select value={advanced.event_type} onChange={e => setAdvanced(p => ({...p, event_type: e.target.value}))}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm bg-white">
              <option>Birthday Party</option><option>Wedding</option><option>Corporate Event</option><option>Baby Shower</option><option>Anniversary</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Days Out</label>
            <input type="number" value={advanced.event_date_offset_days} onChange={e => setAdvanced(p => ({...p, event_date_offset_days: parseInt(e.target.value)||7}))}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Time</label>
            <input type="text" value={advanced.event_time} onChange={e => setAdvanced(p => ({...p, event_time: e.target.value}))}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Guests</label>
            <input type="number" value={advanced.guest_count} onChange={e => setAdvanced(p => ({...p, guest_count: parseInt(e.target.value)||60}))}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm bg-white" />
          </div>
          <div>
            <label className="text-[11px] text-gray-500 mb-1 block">Budget ($)</label>
            <input type="number" value={advanced.budget} onChange={e => setAdvanced(p => ({...p, budget: parseInt(e.target.value)||10000}))}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm bg-white" />
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="presentation-error">
          <span className="shrink-0">!</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* ── Generate Button ── */}
      {!result && (
        <Button
          onClick={generate}
          disabled={!canGenerate || generating}
          className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-medium text-sm disabled:opacity-40 transition-all"
          data-testid="generate-demo-btn"
        >
          {generating ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Building demo scenario...</>
          ) : (
            <><Zap className="h-4 w-4 mr-2" /> Generate Demo{vendorCount > 0 ? ` (${vendorCount} vendor${vendorCount > 1 ? 's' : ''})` : ''}</>
          )}
        </Button>
      )}

      {/* ── Success Result ── */}
      {result && (
        <div className="space-y-4" data-testid="presentation-result">
          {/* Success header */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <h4 className="text-base font-semibold text-green-800">Demo scenario ready</h4>
            </div>
            <p className="text-sm text-green-600 ml-9">
              {result.event_type} on {new Date(result.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Vendor states */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Scenario States</p>
            <div className="space-y-2">
              {result.vendors.map(v => {
                const st = STATE_LABELS[v.state] || { label: v.state, color: 'bg-gray-100 text-gray-600' };
                return (
                  <div key={v.service} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{v.vendor_name}</span>
                      <span className="text-xs text-gray-400">{v.service}</span>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Login Section */}
          <div className="bg-white border-2 border-gray-900 rounded-xl p-4" data-testid="login-guide">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Quick Login</p>

            {/* Customer */}
            <div className="flex items-center justify-between py-2.5 px-3 bg-gray-50 rounded-lg mb-2" data-testid="login-customer">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                  <UserCheck className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Customer</p>
                  <p className="text-sm font-medium text-gray-900">{result.login_guide.customer_name || result.customer.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{result.login_guide.customer_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <CopyBtn text={result.login_guide.customer_email} label="customer-email" />
                <a href={`${appUrl}/login`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                  data-testid="open-login-customer">
                  <ExternalLink className="h-3 w-3" /> Open
                </a>
              </div>
            </div>

            {/* Vendors */}
            {Object.entries(result.login_guide.vendors || {}).map(([slot, info]) => {
              const slotIcon = slot === 'Photography' ? Camera : slot === 'DJ/Music' ? Music : UtensilsCrossed;
              const SlotIcon = slotIcon;
              const slotColor = slot === 'Photography' ? 'bg-blue-100 text-blue-600' : slot === 'DJ/Music' ? 'bg-purple-100 text-purple-600' : 'bg-orange-100 text-orange-600';
              return (
                <div key={slot} className="flex items-center justify-between py-2.5 px-3 bg-gray-50 rounded-lg mb-2" data-testid={`login-vendor-${slot}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${slotColor}`}>
                      <SlotIcon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">{slot}</p>
                      <p className="text-sm font-medium text-gray-900">{info.name}</p>
                      {info.email && <p className="text-xs text-gray-500 font-mono">{info.email}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {info.email && <CopyBtn text={info.email} label={`vendor-${slot}`} />}
                    <a href={`${appUrl}/login`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                      data-testid={`open-login-vendor-${slot}`}>
                      <ExternalLink className="h-3 w-3" /> Open
                    </a>
                  </div>
                </div>
              );
            })}

            {autoGenerate && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700" data-testid="auto-gen-password-hint">
                Demo customer password: <code className="bg-amber-100 px-1 rounded font-mono">Demo123!</code>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-2 px-1">
            {Object.entries(result.counts || {}).filter(([,v]) => v > 0).map(([k, v]) => (
              <span key={k} className="text-[11px] bg-gray-100 text-gray-500 px-2 py-1 rounded-full">{k}: {v}</span>
            ))}
          </div>

          {/* Generate Another */}
          <Button
            onClick={() => { setResult(null); setError(null); }}
            className="w-full bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 py-2.5 rounded-xl font-medium text-sm transition-all"
            data-testid="generate-another-btn"
          >
            Generate Another Demo
          </Button>
        </div>
      )}
    </div>
  );
}
